import { onMfaSignal, supabase } from './supabase';
import type { AuthChangeEvent, EmailOtpType } from '@supabase/supabase-js';
import { useAuthStore } from '@/stores/authStore';
import { assuranceLevels, needsMfa } from './mfaGate';
import { queryClient } from './queryClient';

// Completes an email-link sign-in (signup confirmation, magic link, recovery)
// when the user lands back on the app, so they're signed in automatically and
// never have to type their password again. Two link shapes are handled, in
// priority order:
//
//   1. `?token_hash=…&type=…` — verified with verifyOtp(). This needs NO PKCE
//      code-verifier, so a link opened on a DIFFERENT device or browser than
//      the one that started signup STILL signs the user in. This is the
//      cross-device-safe path and the one to prefer. (Requires the Supabase
//      "Confirm signup" email template to use the {{ .TokenHash }} link — see
//      the auth-setup note in web/README.md.)
//
//   2. `?code=…` — the PKCE flow, exchanged with exchangeCodeForSession(). Only
//      works in the SAME browser that started signup (the verifier lives in its
//      storage); kept for magic links opened in-place / older templates.
//
// The implicit-flow `#access_token=…` hash is handled by detectSessionInUrl in
// the client config. Returns true only when a SIGNUP/EMAIL confirmation just
// succeeded — App seeds its `emailJustConfirmed` state from it so the welcome
// screen shows once. A recovery link still establishes the session but returns
// false, so the PASSWORD_RECOVERY auth event routes to the reset screen instead
// of the welcome celebration.

const TIMEOUT_MS = 8000;

function withTimeout<T>(p: PromiseLike<T>): Promise<T> {
  return Promise.race([
    Promise.resolve(p),
    new Promise<T>((_, reject) => setTimeout(() => reject(new Error('auth link timed out')), TIMEOUT_MS)),
  ]);
}

export async function completeEmailLinkIfPresent(): Promise<boolean> {
  const params = new URLSearchParams(window.location.search);
  const tokenHash = params.get('token_hash');
  const type = (params.get('type') as EmailOtpType | null) ?? undefined;
  const code = params.get('code');

  // A password-reset link signs the user in. The exchange below runs BEFORE
  // the App subscribes to auth events, so the PASSWORD_RECOVERY event that
  // supabase-js emits here would be missed and the user would land in the app
  // as if from a magic link. Catch it now and keep it as a sticky flag that
  // RootRouter turns into the reset screen. Three signals, any one suffices:
  // our own `?recovery=1` marker on the reset redirect, `type=recovery` on a
  // token_hash link, and the PASSWORD_RECOVERY event (PKCE / implicit flow).
  const { data: sub } = supabase.auth.onAuthStateChange((event) => {
    if (event === 'PASSWORD_RECOVERY') useAuthStore.getState().setRecovery(true);
  });
  const markedRecovery = params.get('recovery') === '1' || type === 'recovery';
  stripQueryParam('recovery');
  if (!tokenHash && !code) {
    // Implicit-flow hash links are handled by detectSessionInUrl; the marker
    // still tells us this landing is a reset.
    if (markedRecovery && window.location.hash.includes('access_token')) {
      useAuthStore.getState().setRecovery(true);
    }
    setTimeout(() => sub.subscription.unsubscribe(), 2000);
    return false;
  }

  // Recovery must NOT trigger the "email confirmed" welcome — the
  // PASSWORD_RECOVERY event routes it to the password-reset screen instead.
  const isConfirmation = type !== 'recovery';
  let confirmed = false;
  try {
    if (tokenHash) {
      const { error } = await withTimeout(
        supabase.auth.verifyOtp({ token_hash: tokenHash, type: type ?? 'email' }),
      );
      if (error) throw error;
    } else if (code) {
      const { error } = await withTimeout(supabase.auth.exchangeCodeForSession(code));
      if (error) throw error;
    }
    confirmed = isConfirmation && !markedRecovery;
    if (markedRecovery) useAuthStore.getState().setRecovery(true);
  } catch (e) {
    // Swallow — the router falls back to the landing/sign-in screen and the
    // user can sign in by hand (their account is already confirmed server-side).
    // Log so the dev console surfaces it.
    console.warn('email link sign-in failed:', e);
  } finally {
    // Strip the single-use params from the address bar so a refresh can't replay
    // them (a second attempt fails and can silently sign the user out), and to
    // keep them out of history / shoulder-surfing range.
    stripQueryParam('token_hash');
    stripQueryParam('type');
    stripQueryParam('code');
    sub.subscription.unsubscribe();
  }
  return confirmed;
}

/** Removes one query param via history.replaceState, preserving the rest. */
export function stripQueryParam(key: string): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(key)) return;
    url.searchParams.delete(key);
    const tail =
      url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : '') + url.hash;
    window.history.replaceState({}, '', tail);
  } catch (e) {
    console.warn('URL cleanup failed:', e);
  }
}

// ---------------------------------------------------------------------------
// Two-factor signals (port of mfaSignalControllerProvider,
// lib/providers/mfa_providers.dart; contract §3.4, §4b)
// ---------------------------------------------------------------------------

let steppingUp = false;

/** 42501 mfa_required from any request: this tab's session is aal1 for a
 *  user who enrolled (perhaps elsewhere). Refreshing loads user.factors (the
 *  JWT stays aal1), the TOKEN_REFRESHED event recomputes `mfaPending`, and
 *  RootRouter shows the challenge. Exported for tests. */
export async function stepUpAfterMfaRequired(): Promise<void> {
  if (steppingUp) return;
  steppingUp = true;
  try {
    const { data } = await supabase.auth.refreshSession();
    const session = data.session;
    const levels = assuranceLevels(session);
    // Still no reason to ask for a code although the server refused the
    // session (should not happen): sign out rather than loop. A session that
    // is already aal2 answered a stale request — ignore.
    if (session && levels.current !== 'aal2' && !needsMfa(levels.current, levels.next)) {
      await supabase.auth.signOut({ scope: 'local' });
    }
  } catch {
    // Offline: the next request raises the signal again.
  } finally {
    steppingUp = false;
  }
}

/** Acts on the fetch wrapper's signals (supabase.ts). Installed once by
 *  App; returns the uninstaller. */
export function installMfaSignalHandler(): () => void {
  return onMfaSignal((action) => {
    if (action === 'refreshAndGate') void stepUpAfterMfaRequired();
    if (action === 'adminGate') useAuthStore.getState().setAdminMfaForced(true);
  });
}

/** After the code, a sign-in or a sign-out the admin gate and the cached
 *  mfa_status are stale: ask again. */
export function mfaOnAuthEvent(event: AuthChangeEvent): void {
  if (event === 'MFA_CHALLENGE_VERIFIED' || event === 'SIGNED_OUT' || event === 'SIGNED_IN') {
    useAuthStore.getState().setAdminMfaForced(false);
    void queryClient.invalidateQueries({ queryKey: ['mfaStatus'] });
  }
}
